import React from "react";
import { IItemOrcamento } from "../../../types/IItemOrcamento";
import globalMU from "../../../../globalMU";
import clsx from "clsx";
import { RespostaConsultaItensOrcamento } from "app/types/orcamento/consultaItens/RespostaConsultaItensOrcamento";
import { IColuna } from "app/types/IColuna";
const estilosConsultaitens = require('./consultaItens.module.scss').default;

export type ItemConsultaItensProps = {
    item: RespostaConsultaItensOrcamento["Itens"][0]
    numeroCasasDecimaisQuantidade: number
    numeroCasasDecimaisPreco: number
    corPorAmbiente: { [ambiente: string]: string }
    colunasSelecionadas: IColuna[]
}

export const ItemConsultaItens = React.memo((props: ItemConsultaItensProps) => {

    const item = props.item;

    // Toda celula sai por aqui: garante `id` (e `key`) em cada `td`, no padrao
    // ItemConsultaItens_<Coluna>_<Index> — o Index deixa o id unico por linha.
    const _celula = (
        sufixoId: string,
        conteudo: React.ReactNode,
        extras?: { className?: string, style?: React.CSSProperties }
    ) => {
        const idCelula = `ItemConsultaItens_${sufixoId}_${item.Index}`;

        return <td key={idCelula} id={idCelula} className={extras?.className} style={extras?.style}>
            {conteudo}
        </td>;
    };

    const _celulaNumerica = (sufixoId: string, conteudo: React.ReactNode, style?: React.CSSProperties) =>
        _celula(sufixoId, conteudo, { className: estilosConsultaitens.colunaValorNumerico, style });

    // "% Desconto*": (preço de cadastro - preço unitário) / preço de cadastro * 100, TRUNCADO em 2
    // casas (não arredondado — daí o Math.trunc em vez de deixar a formatação arredondar).
    // Sem preço de cadastro não há base de cálculo: a célula fica vazia, em vez de 0 ou infinito.
    const _obterPercentualDescontoSobrePrecoCadastro = () => {
        const precoCadastro = item.PrecoCadastro;

        if (precoCadastro == null || precoCadastro === 0) return "";

        const percentual = (precoCadastro - item.PrecoProduto) / precoCadastro * 100;

        return globalMU.formatarValorNumerico(Math.trunc(percentual * 100) / 100, 2, false);
    };

    const renderizarCelula = (coluna: IColuna) => {
        switch (coluna.Nome) {
            case "CodigoProduto":
                return _celula("CodigoProduto", item.CodigoProduto);
            case "DescricaoProduto":
                return _celula("DescricaoProduto", item.DescricaoProduto);
            case "Quantidade":
                return _celulaNumerica("Quantidade", globalMU.formatarValorNumerico(item.Quantidade, props.numeroCasasDecimaisQuantidade, false));
            case "PrecoProduto":
                // Desconto pendente de liberação: preço em vermelho (mesmo vermelho da célula de
                // markup abaixo do permitido). `DescontoPendente` vem da Fenix
                // (ItensPedidoVenda.prg -> ObterDescontoPendente) via ItemOrcamentoModalContract.
                return _celulaNumerica(
                    "PrecoProduto",
                    globalMU.formatarValorNumerico(item.PrecoProduto, props.numeroCasasDecimaisPreco, false),
                    { color: item.DescontoPendente === true ? 'var(--mu-vermelho-texto, #f00)' : undefined }
                );
            case "ValorTotal":
                return _celulaNumerica("ValorTotal", globalMU.formatarValorNumerico(item.ValorTotal, 3, false));
            case "PrecoUnitarioTabela":
                return _celulaNumerica("PrecoUnitarioTabela", globalMU.formatarValorNumerico(item.PrecoUnitarioTabela, props.numeroCasasDecimaisPreco, false));
            case "PrecoUnitarioTabelaAsterisco":
                return _celulaNumerica("PrecoUnitarioTabelaAsterisco", globalMU.formatarValorNumerico(item.PrecoCadastro, props.numeroCasasDecimaisPreco, false));
            case "PrecoTotalTabela":
                return _celulaNumerica("PrecoTotalTabela", globalMU.formatarValorNumerico(item.PrecoTotalTabela, props.numeroCasasDecimaisPreco, false));
            case "PrecoUnitarioAVista":
                return _celulaNumerica("PrecoUnitarioAVista", globalMU.formatarValorNumerico(item.PrecoUnitarioAVista, props.numeroCasasDecimaisPreco, false));
            case "PrecoTotalAVista":
                return _celulaNumerica("PrecoTotalAVista", globalMU.formatarValorNumerico(item.PrecoTotalAVista, props.numeroCasasDecimaisPreco, false));
            case "PercentualMarkUp":
                return _celulaNumerica(
                    "PercentualMarkUp",
                    globalMU.formatarValorNumerico(item.PercentualMarkUp, 2, false),
                    { color: item.MarkUpAbaixoDoPermitido ? '#f00' : undefined }
                );
            case "MarkupMinimo":
                return _celulaNumerica("MarkupMinimo", globalMU.formatarValorNumerico(item.MarkupMinimo, 2, false));
            case "PrecoVendaMinimoCustoReal":
                return _celulaNumerica("PrecoVendaMinimoCustoReal", globalMU.formatarValorNumerico(item.PrecoVendaMinimoCustoReal, 2, false));
            case "PercentualDesconto":
                return _celulaNumerica("PercentualDesconto", globalMU.formatarValorNumerico(item.PercentualDesconto, 2, false));
            case "PercentualDescontoAsterisco":
                return _celulaNumerica("PercentualDescontoAsterisco", _obterPercentualDescontoSobrePrecoCadastro());
            case "CodigoTipoEntrega":
                return _celula("CodigoTipoEntrega", item.CodigoTipoEntrega);
            case "Carga":
                return _celula("Carga", item.Carga);
            case "CodigoFilialRetirada":
                return _celula("CodigoFilialRetirada", item.CodigoFilialRetirada);
            case "CodigoFilialTransferencia":
                return _celula("CodigoFilialTransferencia", item.CodigoFilialTransferencia);
            case "ServicoAgregado":
                return _celula("ServicoAgregado", item.ServicoAgregado);
            case "TipoFaturamento":
                return _celula("TipoFaturamento", item.TipoFaturamento);
            case "VendaSobEncomenda":
                return _celula("VendaSobEncomenda",
                    item.CodigoTipoEntrega === "C" ? "" : item.VendaSobEncomenda && item.VendaSobEncomenda === true ? "Sim" : "Não");
            case "EncomendaExpressa":
                return _celula("EncomendaExpressa",
                    item.CodigoTipoEntrega === "C" ? "" : item.VendaSobEncomenda && item.VendaSobEncomenda === true ? item.EncomendaExpressa === true ? "Sim " : "Não" : "");
            case "EntregaPosterior":
                return _celula("EntregaPosterior",
                    item.CodigoTipoEntrega === "C" ? "" : item.VendaSobEncomenda && item.VendaSobEncomenda === true ? item.EntregaPosterior === 1 ? "Sim" : "Não" : "");
            case "Mostruário":
                return _celula("Mostruario", item.Mostruario === true ? "Sim" : "Não", { className: "_text-center" });
            case "QuantidadeDevolvida":
                return _celulaNumerica("QuantidadeDevolvida", globalMU.formatarValorNumerico(item.QuantidadeDevolvida, props.numeroCasasDecimaisQuantidade, false));
            case "NfDevolucao":
                return _celula("NfDevolucao", item.NfDevolucao);
            case "Pendencia":
                return _celula("Pendencia", item.Pendencia);
            case "Lote":
                return _celula("Lote", item.Lote);
            case "Volume":
                return _celula("Volume", item.Volume);
            case "Peso":
                return _celula("Peso", item.Peso);
            case "Ambiente":
                return _celula("Ambiente",
                    <span
                        id={`ItemConsultaItens_Ambiente_Texto_${item.Index}`}
                        style={{
                            backgroundColor: item.Ambiente in props.corPorAmbiente ? props.corPorAmbiente[item.Ambiente] : undefined
                        }}
                    >
                        {item.Ambiente}
                    </span>
                );
            case "CentroDeCusto":
                return _celula("CentroDeCusto", item.NomeCentroCusto);
            case "PedidoCompra":
                return _celula("PedidoCompra", item.PedidoCompra);
            case "LinhaOrdemCompra":
                return _celula("LinhaOrdemCompra", item.PedidoCompraCliente);
            case "NomeVendedor":
                return _celula("NomeVendedor", item.NomeVendedor);
            default:
                return coluna.ehEstoqueFilial
                    ? _celula(
                        `EstoqueFilial_${coluna.Nome}`,
                        (item.ListEstoqueProduto?.Estoques ?? []).find(e => `F${e.CodigoFilial}` === coluna.Nome)?.QuantidadeDisponivel
                    )
                    : _celula(`Vazio_${coluna.Nome}`, null);
        }
    };

    return (
        <tr key={`item-${item.Index}`} id={`ItemConsultaItens_Linha_${item.Index}`}>
            {props.colunasSelecionadas.map(renderizarCelula)}
        </tr>
    );
});
